import React from 'react';
import classnames from 'classnames';
import { Image } from 'components/Image/Image';
import { useConfig } from 'hook/config.hook';
import { get } from 'helpers/objectHelpers';
import Label from 'components/form/Label/Label';
import TextFromConfig from 'components/TextFromConfig';
import { CSTooltip } from 'components/Tooltip/CSToolTip';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { GUARANTEE } from 'helpers/approvalOdds.helper';
import './ApprovalOddsBadge.scss';

const badges = {
  OUTSTANDING: (
    <Image datatestid="outStanding" className={'outStandingBadgeIcon'} />
  ),
  EXCELLENT: <Image datatestid="excellent" className={'excellentBadgeIcon'} />,
  GREAT: <Image datatestid="great" className={'greatBadgeIcon'} />,
  FAIR: <Image datatestid="fair" className={'approvalOddsFairIcon'} />,
  POOR: <Image datatestid="poor" className={'approvalOddsVeryPoorIcon'} />,
  GOOD: <Image datatestid="good" className={'approvalOddsGoodIcon'} />,
  VERY_GOOD: (
    <Image datatestid="veryGood" className={'approvalOddsVeryGoodIcon'} />
  ),
  GUARANTEE: (
    <Image
      datatestid="guarantee"
      className={'approvalOddsSesameGuaranteeIcon'}
    />
  ),
  PRE_APPROVED: (
    <Image
      datatestid={'pre_approved'}
      className={'redesignBagdePreApprovedIcon'}
    />
  ),
};

const carouselBadge = {
  OUTSTANDING: (
    <Image
      datatestid="outStanding"
      className={'outStandingBadgeCarouselIcon'}
    />
  ),
  EXCELLENT: (
    <Image datatestid="excellent" className={'excellentBadgeCarouselIcon'} />
  ),
  GREAT: <Image datatestid="great" className={'greatBadgeCarouselIcon'} />,
  POOR: <Image datatestid="poor" className={'approvalOddsVeryPoorIcon'} />,
  FAIR: <Image datatestid="fair" className={'approvalOddsFairIcon'} />,
  GOOD: <Image datatestid="good" className={'approvalOddsGoodIcon'} />,
  VERY_GOOD: (
    <Image datatestid="veryGood" className={'approvalOddsVeryGoodIcon'} />
  ),
  GUARANTEE: (
    <Image
      datatestid="guarantee"
      className={'approvalOddsSesameGuaranteeIcon'}
      width="96"
    />
  ),
  PRE_APPROVED: (
    <Image
      datatestid={'pre_approved'}
      className={'redesignBagdePreApprovedIcon'}
    />
  ),
};

const redesignCardlBadge = {
  OUTSTANDING: (
    <Image
      datatestid="outStanding"
      className={'outStandingBadgeCarouselIcon'}
      width="106"
    />
  ),
  EXCELLENT: (
    <Image
      datatestid="excellent"
      className={'excellentBadgeCarouselIcon'}
      width="106"
    />
  ),
  GREAT: (
    <Image
      datatestid="great"
      className={'greatBadgeCarouselIcon'}
      width="106"
    />
  ),
  POOR: <Image datatestid="poor" className={'approvalOddsVeryPoorIcon'} />,
  FAIR: <Image datatestid="fair" className={'approvalOddsFairIcon'} />,
  GOOD: <Image datatestid="good" className={'approvalOddsGoodIcon'} />,
  VERY_GOOD: (
    <Image datatestid="veryGood" className={'approvalOddsVeryGoodIcon'} />
  ),
  GUARANTEE: (
    <Image
      datatestid="guarantee"
      className={'approvalOddsSesameGuaranteeIcon'}
    />
  ),
  PRE_APPROVED: (
    <Image
      datatestid={'pre_approved'}
      className={'redesignBagdePreApprovedIcon'}
    />
  ),
};

const BadgeSwitch = props => {
  const { observedApprovalOdds, isCarousel, isRedesignCard } = props;
  if (observedApprovalOdds) {
    if (isCarousel) {
      return carouselBadge[observedApprovalOdds] ?? <></>;
    } else if (isRedesignCard) {
      return redesignCardlBadge[observedApprovalOdds] ?? <></>;
    } else {
      return badges[observedApprovalOdds] ?? <></>;
    }
  } else {
    return <></>;
  }
};

const TooltipTitleComponent = props => {
  const {
    titleLabel,
    tooltip,
    isOnlyTooltip,
    customClass = 'fontNeutral300 text-12 margt2 m-text-right',
    tipOverlayClassName,
  } = props;
  return (
    <Label type="Subtitle-3" className={customClass} tabIndex={-1}>
      <CSTooltip
        title={tooltip}
        isInline={true}
        className="predictedLimit__tooltipContainer"
        tooltipName={titleLabel}
        overlayClassName={tipOverlayClassName}
        placement="bottomRight"
      >
        {!isOnlyTooltip && <span>{titleLabel} </span>}
        <QuestionCircleOutlined
          style={{
            display: 'inline-block',
            position: 'relative',
            top: -3,
          }}
        />
      </CSTooltip>
    </Label>
  );
};

const ApprovalOddsBadge = props => {
  const {
    labelCustomClass,
    badgeCustomClass,
    offerMetadata,
    isCarousel,
    isRedesignCard,
    isRedesignDetail,
    isTooltipSorrounded,
    tipOverlayClassName,
  } = props;
  const { observedApprovalOdds, hideApprovalOdds, approvalOdds } =
    offerMetadata;

  const isGuarantee = observedApprovalOdds === GUARANTEE;
  const { config } = useConfig();

  const tooltip =
    offerMetadata?.preApprovalTooltipText ??
    get(config, TextFromConfig.paths.DISCLAIMER_AO, '');

  const approvalOddsAvailable =
    observedApprovalOdds && observedApprovalOdds !== 'NA';

  const approvalOddsBadge =
    observedApprovalOdds === 'PRE_APPROVED'
      ? 'OUTSTANDING'
      : observedApprovalOdds;

  if (!hideApprovalOdds && approvalOddsAvailable) {
    if (isTooltipSorrounded) {
      return (
        <FlexContainer className={'flex-column'}>
          <CSTooltip
            title={tooltip}
            isInline={true}
            className="flex-column"
            tooltipName={'Approval odds'}
            placement="bottomRight"
            overlayClassName={tipOverlayClassName}
          >
            {(!isGuarantee || isRedesignDetail) && (
              <Label
                type="Subtitle-3"
                className={classnames(labelCustomClass, 'margt2')}
                tabIndex={-1}
              >
                <span className={'margr4'}>Approval odds</span>
                <QuestionCircleOutlined
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                    top: -3,
                  }}
                />
              </Label>
            )}
          </CSTooltip>
          <FlexContainer
            className={classnames(
              'items-align-center ApprovalOddsBadge margL4',
              badgeCustomClass,
              {
                margt26: isGuarantee,
              },
            )}
          >
            <BadgeSwitch
              isCarousel={isCarousel}
              observedApprovalOdds={approvalOddsBadge}
              isRedesignCard={isRedesignCard}
              approvalOdds={approvalOdds}
            />
            {isGuarantee && !isRedesignDetail && (
              <Label type="Subtitle-3" className={labelCustomClass}>
                <QuestionCircleOutlined
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                    top: -3,
                  }}
                />
              </Label>
            )}
          </FlexContainer>
        </FlexContainer>
      );
    } else {
      return (
        <FlexContainer className={'flex-column'}>
          {(!isGuarantee || isRedesignDetail) && (
            <TooltipTitleComponent
              titleLabel={'Approval odds'}
              tooltip={tooltip}
              customClass={labelCustomClass}
              tipOverlayClassName={tipOverlayClassName}
            />
          )}
          <FlexContainer
            className={classnames(
              'items-align-center ApprovalOddsBadge',
              badgeCustomClass,
              {
                margt26: isGuarantee,
              },
            )}
          >
            <BadgeSwitch
              isCarousel={isCarousel}
              observedApprovalOdds={approvalOddsBadge}
              isRedesignCard={isRedesignCard}
              approvalOdds={approvalOdds}
            />
            {isGuarantee && !isRedesignDetail && (
              <TooltipTitleComponent
                titleLabel={'Approval odds'}
                tooltip={tooltip}
                isOnlyTooltip={true}
                customClass={labelCustomClass}
              />
            )}
          </FlexContainer>
        </FlexContainer>
      );
    }
  } else {
    return <></>;
  }
};

export default ApprovalOddsBadge;
